import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';

/** Utilities */
import { decryptUrlData } from '../../../services/CryptoService';

/** Common Components */
import Avatar from '../../../components/ui/avatar/Avtar';
import Tooltip from "../../../components/ui/tooltip/Tooltip";
import CardSection from '../../../components/ui/card/CardSection';
import Iconify from '../../../layouts/components/iconify/iconify';
import DataLoader from '../../../components/ui/dataLoader/DataLoader';
import { RenderChart } from '../../../layouts/components/chart/RenderChart';

/** RTK Query */
import { useLazyGetProjectOverviewMemberDetailsQuery } from '../../../app/services/projectOverviewAPI';
import { useLazyGetProjectOverviewMemberTaskDetailsQuery, useLazyGetProjectOverviewStatusDetailsQuery } from '../../../app/services/projectOverviewAPI';

/** CSS Files */
import "../ProjectOverview.scss";

const defaultStatus = ["toDo", "open", "inProgress", "complete", "done", "reOpen", "codeReview", "test"]

const ProjectOverviewDetails = () => {
    const { id } = useParams();

    const projectData = id ? decryptUrlData(id) : {};

    const { projectId, projectCode, projectName } = projectData;

    const cardData = [
        { key: projectCode, value: projectName },
    ];

    const [openUsers, setOpenUsers] = useState({});
    const [taskColors, setTaskColors] = useState(null);
    const [polarChartData, setPolarChartData] = useState();
    const [barChartData, setBarChartData] = useState();
    const [projectMemberTaskData, setProjectMemberTaskData] = useState();

    /** Polar Chart RTK */
    const [getProjectPolarChartDetails, {
        isLoading: isProjectPolarChartDetailsLoading,
        isFetching: isProjectPolarChartDetailsFetching,
        isSuccess: isProjectPolarChartDetailsSuccess,
        data: projectPolarChartDetailsData,
    },] = useLazyGetProjectOverviewStatusDetailsQuery();

    /** Bar Chart RTK */
    const [getProjectBarChartDetails, {
        isLoading: isProjectBarChartDetailsLoading,
        isFetching: isProjectBarChartDetailsFetching,
        isSuccess: isProjectBarChartDetailsSuccess,
        data: projectBarChartDetails,
    },] = useLazyGetProjectOverviewMemberDetailsQuery();

    const [getProjectOverviewMemberTaskDetails, {
        isFetching: isProjectOverviewMemberTaskDetailsFetching,
        isSuccess: isProjectOverviewMemberTaskDetailsSuccess,
        data: ProjectOverviewMemberTaskDetailsData,
    },] = useLazyGetProjectOverviewMemberTaskDetailsQuery();

    useEffect(() => {
        getProjectPolarChartDetails(projectId);
        getProjectBarChartDetails(projectId)
        getProjectOverviewMemberTaskDetails(projectId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    useEffect(() => {
        if (!isProjectPolarChartDetailsFetching && isProjectPolarChartDetailsSuccess && projectPolarChartDetailsData) {
            setPolarChartData(projectPolarChartDetailsData);
        }
    }, [isProjectPolarChartDetailsFetching, isProjectPolarChartDetailsSuccess, projectPolarChartDetailsData])

    useEffect(() => {
        if (!isProjectOverviewMemberTaskDetailsFetching && isProjectOverviewMemberTaskDetailsSuccess && ProjectOverviewMemberTaskDetailsData) {

            const getMemberTaskStatus = (statusArr) => {
                return statusArr.map((item) => ({
                    ...item,
                    className: unformatLabel(item.statusName),
                    count: item.totalTask || 0,
                }));
            }

            let updatedMemeberData = ProjectOverviewMemberTaskDetailsData.map((item) => ({
                ...item,
                projectStatus: getMemberTaskStatus(item.projectStatus),
                progressData: calculateProgress(getMemberTaskStatus(item.projectStatus)),
            }))


            setProjectMemberTaskData(updatedMemeberData);
        }
    }, [isProjectOverviewMemberTaskDetailsFetching, isProjectOverviewMemberTaskDetailsSuccess, ProjectOverviewMemberTaskDetailsData])


    const getTaskStatuses = (taskStatusObj) => {
        if (!taskStatusObj || typeof taskStatusObj !== 'object') return [];

        return Object.entries(taskStatusObj)
            .filter(([key]) => key !== 'userId')
            .map(([key, count]) => ({
                label: formatLabel(key),
                count: count || 0,
                className: key,
            }));
    };

    const calculateProgress = (taskData) => {
        const totalCount = taskData.reduce((sum, { count }) => sum + (count || 0), 0);

        return taskData.map(({ className, count }) => ({
            className,
            width: totalCount > 0 ? `${((count / totalCount) * 100)}%` : "0%",
            count: count && count > 1 ? `${count} Tasks` : `${count} Task`,
        }));
    }

    const transformData = (data) => {
        return data.map((user) => {

            const projectStatus = getTaskStatuses(user.projectStatus?.[0]);

            const progressData = calculateProgress(projectStatus);

            return {
                userId: user.userId,
                name: user.userName || null,
                workHours: user.totalHours,
                teamName: user.teamName,
                projectStatus,
                progressData

            };
        });
    };

    const formatLabel = (key) => {
        // Insert space before capital letters and capitalize the result
        return key
            .replace(/([a-z])([A-Z])/g, '$1 $2') // e.g., "notStarted" → "not Started"
            .replace(/^./, str => str.toUpperCase()); // capitalize first letter
    };

    const unformatLabel = (label) => {
        return label.toLowerCase().split(' ').map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)).join('');
    };

    useEffect(() => {
        if (!isProjectBarChartDetailsFetching && isProjectBarChartDetailsSuccess && projectBarChartDetails) {
            const userData = transformData(projectBarChartDetails);
            console.log(userData, "userData..");
            const transformed = projectBarChartDetails.map(user => {
                const result = {
                    labelId: user.userId,
                    label: user.userName
                };

                user.projectStatus.forEach(status => {
                    result[status.label] = status.data;
                });

                return result;
            });
            console.log(transformed, "transformed..");
            setBarChartData(transformed);

            const allStatuses = projectBarChartDetails.flatMap(user => user.projectStatus);

            const visitedStatus = new Set();
            const uniqueStatusColors = [];

            allStatuses.forEach(status => {
                if (!visitedStatus.has(status.label)) {
                    visitedStatus.add(status.label);
                    uniqueStatusColors.push({ label: status.label, value: status.color })
                }
            })


            const statusColorObject = uniqueStatusColors.reduce((acc, curr) => {
                const key = curr.label.toLowerCase(); // convert label to lowercase
                acc[key] = curr.value;
                return acc;
            }, {});


            setTaskColors(statusColorObject)

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProjectBarChartDetailsFetching, isProjectBarChartDetailsSuccess, projectBarChartDetails]);

    // Toggle function for user card
    const toggleUserCard = (userId) => {
        setOpenUsers((prev) => ({
            ...prev,
            [userId]: !prev[userId], // Toggle only the clicked user's card
        }));
    };

    return (
        <div className="col-12 project-overview-sec center-top-space">
            <div className="chart-section">
                <CardSection
                    cardTitle="Project Overview"
                    cardInfo={true}
                    cardInfoWrapper={cardData}
                >
                    <div className="row">
                        <div className="col-4">
                            {isProjectPolarChartDetailsLoading ? (<DataLoader />) : (
                                <div className="project-overview-chart">
                                    <h3 className="title">Project Overview</h3>
                                    <RenderChart data={polarChartData} chartType="polarArea" showLegend={true} />
                                </div>
                            )}
                        </div>

                        <div className="col-8">
                            {isProjectBarChartDetailsLoading ? (<DataLoader />) : (
                                <div className="resorse-overview-chart">
                                    <h3 className="title">Project Team Overview</h3>
                                    <RenderChart data={barChartData} taskColors={taskColors} chartType="bar" showLegend={true} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="row resource-detail mt-5">
                        {projectMemberTaskData &&
                            projectMemberTaskData.map((user) => (
                                <div className="col-4" key={user?.userId}>
                                    <div className="user-taskdetail-card" >
                                        <button
                                            className="user-detail-section"
                                            onClick={() => toggleUserCard(user?.userId)}
                                        >
                                            <div className="user-detail">
                                                <Avatar alt={user?.userName} size={"medium"} />
                                                <div className="user-name">{user?.userName}</div>
                                            </div>
                                            <div className="right-section">
                                                <div className="user-status-badge">
                                                    Work Hrs: {user?.totalworkingHours}
                                                </div>
                                                <div
                                                    className={`user-detail-dropdown ${openUsers[user?.userId] ? "active" : ""}`}
                                                >
                                                    <Iconify icon="ep:arrow-down-bold" />
                                                </div>
                                            </div>
                                        </button>

                                        {openUsers[user?.userId] && (
                                            <div className="task-detail-collection active">
                                                <div className="task-detail-title">Task Details</div>
                                                <div className="task-detail-container">
                                                    <div className="task-detail-section">
                                                        {user.projectStatus.map((task, index) => (
                                                            <div className="task-status-detail" key={index}>
                                                                <div className="task-status-number">
                                                                    {task.totalTask}
                                                                </div>
                                                                <div
                                                                    className={`task-status ${defaultStatus.includes(task.className) ? task.className : "default"}`}
                                                                >
                                                                    <Iconify icon="icon-park-outline:dot" />
                                                                    {task.statusName}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="multicolor-progress-bar-container">
                                                    <div className="multicolor-progress-bar">
                                                        {user?.progressData?.map((progress, index) => (
                                                            <div
                                                                key={index + 1}
                                                                id={`tooltip-top-${index}`}
                                                                className={`progress-value ${progress?.className}`}
                                                                style={{ width: progress?.width }}
                                                            >
                                                                <Tooltip
                                                                    label={progress?.count}
                                                                    targetId={`tooltip-top-${index}`}
                                                                    position="top"
                                                                    className="tooltip"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </CardSection>
            </div>
        </div>
    )
}

export default ProjectOverviewDetails